import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { metricsById } from "@/config/metricsConfig";
import MetricCounter from "@/components/shared/MetricCounter";
import {
  getCaptecPerformanceProfile,
  isFastScrollTrigger,
} from "./scrollPerformance";

gsap.registerPlugin(ScrollTrigger);

const cosmosBase = "/assets/cosmos/";
const planet = `${cosmosBase}planet-earth.png`;
const logo = `${cosmosBase}captec-orbit-logo.png`;
const card = `${cosmosBase}orbit-card.png`;
const iconBase = "/assets/cosmos/";

const stats = [
  {
    pos: "pos-top-left",
    ...metricsById.transactions,
    desc: (
      <>
        TRANSACCIONES PROCESADAS
        <br />
        / AÑO
      </>
    ),
    icon: "transaccionesPNG.png",
  },
  {
    pos: "pos-bottom-left",
    ...metricsById.availability,
    desc: <>DISPONIBILIDAD</>,
    icon: "disponibilidadPNG.png",
  },
  {
    pos: "pos-top-center",
    display: "PCI DSS",
    desc: <>CERTIFICACIÓN</>,
    icon: "certificadoPNG.png",
    small: true,
  },
  {
    pos: "pos-bottom-center",
    display: "24/7",
    desc: <>OPERACIÓN CONTINUA</>,
    icon: "operacion-reloj.png",
  },
  {
    pos: "pos-top-right",
    ...metricsById.countries,
    desc: (
      <>
        PAÍSES CON
        <br />
        PRESENCIA
      </>
    ),
    icon: "paisesPresentesPNG.png",
  },
  {
    pos: "pos-mid-right",
    ...metricsById.institutions,
    desc: (
      <>
        INSTITUCIONES
        <br />
        FINANCIERAS
      </>
    ),
    icon: "entidadesFinancierasPNG.png",
  },
  {
    pos: "pos-bottom-right",
    ...metricsById.cards,
    desc: <>TARJETAS PROCESADAS</>,
    icon: "tarjetasProcesadasPNG.png",
  },
];

const StatValue = ({ stat, countersActive }) => {
  if (stat.display) return stat.display;

  return (
    <MetricCounter
      value={stat.value}
      prefix={stat.prefix || ""}
      suffix={stat.suffix || ""}
      active={countersActive}
      duration={1850}
    />
  );
};

export default function CosmosStats({
  embedded = false,
  countersActive,
  logoTargetRef,
}) {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        root.classList.toggle("is-cosmos-visible", entry.isIntersecting);
      },
      { rootMargin: "80px 0px", threshold: 0.02 }
    );

    visibilityObserver.observe(root);

    if (embedded) {
      return () => visibilityObserver.disconnect();
    }

    const profile = getCaptecPerformanceProfile();
    const { lowResource, reducedMotion, fastScrollVelocity } = profile;

    const ctx = gsap.context(() => {
      const planetElements = root.querySelectorAll(".planet-img, .africa-logo");
      const statElements = root.querySelectorAll(".stat-dato");
      const orbitElements = root.querySelectorAll(".orbit-ring");

      const applyFinalState = () => {
        gsap.set(planetElements, { scale: 1, opacity: 1 });
        gsap.set(statElements, {
          opacity: 1,
          x: 0,
          y: 0,
          scale: 1,
          rotation: 0,
        });
        gsap.set(orbitElements, { opacity: 1, scale: 1 });
        root.classList.add("is-cosmos-animation-complete");
      };

      if (reducedMotion) {
        applyFinalState();
        return;
      }

      gsap.set(planetElements, {
        scale: lowResource ? 0.9 : 0.78,
        opacity: 0,
        transformOrigin: "50% 50%",
      });
      gsap.set(statElements, {
        opacity: 0,
        x: (index) =>
          lowResource ? (index % 2 === 0 ? -28 : 28) : index % 2 === 0 ? -90 : 90,
        y: (index) =>
          lowResource ? (index < 3 ? -16 : 16) : index < 3 ? -42 : 42,
        scale: lowResource ? 0.98 : 1.08,
        rotation: lowResource ? 0 : 3,
        transformOrigin: "50% 50%",
      });
      gsap.set(orbitElements, {
        opacity: 0,
        scale: lowResource ? 1 : 1.08,
        transformOrigin: "50% 50%",
      });

      const timeline = gsap.timeline({ paused: true });
      timeline
        .to(planetElements, {
          scale: 1,
          opacity: 1,
          duration: lowResource ? 0.3 : 0.5,
          ease: "power2.out",
        })
        .to(
          statElements,
          {
            opacity: 1,
            x: 0,
            y: 0,
            scale: 1,
            rotation: 0,
            duration: lowResource ? 0.26 : 0.46,
            stagger: lowResource ? 0.012 : 0.035,
            ease: "power2.out",
          },
          "-=0.22"
        )
        .to(
          orbitElements,
          {
            opacity: 1,
            scale: 1,
            duration: lowResource ? 0.2 : 0.36,
            stagger: lowResource ? 0.01 : 0.045,
            ease: "power2.out",
          },
          "-=0.18"
        );

      let trigger = null;
      trigger = ScrollTrigger.create({
        trigger: root,
        start: "top 94%",
        end: "bottom top",
        once: true,
        fastScrollEnd: fastScrollVelocity,
        invalidateOnRefresh: true,
        refreshPriority: -20,
        onEnter: (self) => {
          if (isFastScrollTrigger(self, fastScrollVelocity)) {
            timeline.progress(1).pause();
            applyFinalState();
          } else {
            timeline.play(0);
          }
        },
        onLeave: () => {
          timeline.progress(1).pause();
          applyFinalState();
          trigger?.kill();
        },
      });
    }, root);

    return () => {
      visibilityObserver.disconnect();
      ctx.revert();
    };
  }, [embedded]);

  const wrapperClass = `cosmos-wrapper ${
    embedded
      ? "cosmos-wrapper--hero is-cosmos-animation-complete"
      : ""
  }`;

  return (
    <div
      className={wrapperClass}
      id={embedded ? "hero-cosmos" : "cosmos-trigger"}
      ref={rootRef}
    >
      <div className="cosmos-header" data-immersive-final={embedded ? "" : undefined}>
        <h2 className="cosmos-scale-title">Escala que ya opera</h2>
      </div>

      <div className="cosmos-content">
        <div className="solar-system" data-immersive-final={embedded ? "" : undefined}>
          <img src={planet} className="planet-img" alt="Mundo" decoding="async" />
          {embedded ? (
            <div
              ref={logoTargetRef}
              className="immersive-final-logo-target cosmos-hero-logo-target"
              aria-hidden="true"
            >
              <img
                src={logo}
                className="cosmos-hero-color-logo"
                alt=""
                decoding="async"
              />
            </div>
          ) : (
            <img src={logo} className="africa-logo" alt="Captec Logo" decoding="async" />
          )}
          {[1, 2, 3, 4].map((ring) => (
            <div className={`orbit-ring cosmos-orbit--${ring}`} key={ring}>
              <div className="orbiting-item">
                <img src={card} alt="" decoding="async" aria-hidden="true" />
              </div>
            </div>
          ))}
        </div>

        {stats.map((stat) => (
          <div
            className={`stat-box ${stat.pos} stat-dato`}
            key={stat.pos}
            data-immersive-final={embedded ? "" : undefined}
          >
            <div className="stat-text-group">
              {stat.pos === "pos-top-center" && (
                <p className="stat-desc">{stat.desc}</p>
              )}
              <h3
                className={`stat-num cian ${stat.small ? "stat-num--compact" : ""}`}
              >
                <span aria-hidden="true"><StatValue stat={stat} countersActive={countersActive} /></span>
                <span className="sr-only">{stat.display || `${stat.prefix || ""}${stat.value}${stat.suffix || ""}`}</span>
              </h3>
              {stat.pos !== "pos-top-center" && (
                <p className="stat-desc">{stat.desc}</p>
              )}
            </div>
            <img
              src={`${iconBase}${stat.icon}`}
              alt=""
              className="stat-icon-img"
              decoding="async"
              aria-hidden="true"
            />
          </div>
        ))}
      </div>
    </div>
  );
}
